import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Input, Select, Avatar, Tag, Modal, message,
  Space, Typography, Button,
} from 'antd';
import { SearchOutlined, UserOutlined, CheckCircleOutlined, ClockCircleOutlined, SyncOutlined, SendOutlined } from '@ant-design/icons';
import { getHelpSuggestionsApi, updateHelpStatusApi } from '../../api/admin';
import { USER_IMAGE } from '../../utils/constants';
import { formatDate } from '../../utils/formatters';
import SendNotificationModal from '../../components/SendNotificationModal';

const { Title, Text, Paragraph } = Typography;

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
];

const STATUS_CONFIG = {
  pending:     { color: 'orange',  icon: <ClockCircleOutlined />,  label: 'Pending'     },
  in_progress: { color: 'blue',    icon: <SyncOutlined spin />,    label: 'In Progress' },
  resolved:    { color: 'green',   icon: <CheckCircleOutlined />,  label: 'Resolved'    },
};

export default function AdminHelp() {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [meta, setMeta]           = useState({ total: 0, current_page: 1, per_page: 20 });
  const [search, setSearch]       = useState('');
  const [status, setStatus]       = useState('pending');
  const [detailItem, setDetailItem] = useState(null);
  const [updating, setUpdating]   = useState(false);
  const [notifUser, setNotifUser] = useState(null);

  const fetchHelp = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, per_page: meta.per_page, sort_by: 'created_at', sort_dir: 'desc' };
      if (search) params.search = search;
      if (status) params.status = status;
      const res = await getHelpSuggestionsApi(params);
      setItems(res.data.data || []);
      setMeta(res.data.meta || meta);
    } catch {
      message.error('Failed to load help requests');
    } finally {
      setLoading(false);
    }
  }, [search, status, meta.per_page]);

  useEffect(() => { fetchHelp(1); }, [search, status]);

  const handleStatusChange = async (item, newStatus) => {
    setUpdating(true);
    try {
      await updateHelpStatusApi({ id: item.id, status: newStatus });
      message.success('Status updated');
      setDetailItem((prev) => prev ? { ...prev, status: newStatus } : null);
      fetchHelp(meta.current_page);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const columns = [
    {
      title: 'User',
      key: 'user',
      width: 180,
      render: (_, r) => (
        <Space>
          <Avatar
            src={r.user_photo ? USER_IMAGE(r.user_photo) : null}
            icon={<UserOutlined />}
            size={32}
            style={{ background: '#e8f5e9', color: '#1a6b2a' }}
          />
          <div>
            <div style={{ fontWeight: 600, fontSize: 12 }}>{r.user_name || '-'}</div>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>{r.mobile}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      width: 200,
      render: (v, r) => (
        <Button type="link" style={{ padding: 0, fontWeight: 600, fontSize: 13, textAlign: 'left', height: 'auto' }}
          onClick={() => setDetailItem(r)}>
          {v}
        </Button>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      width: 260,
      render: (v) => (
        <Text style={{ fontSize: 12, color: '#6b7280' }} ellipsis={{ tooltip: v }}>{v}</Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 140,
      render: (v, r) => (
        <Select
          value={v}
          size="small"
          style={{ width: 130 }}
          onChange={(val) => handleStatusChange(r, val)}
          options={STATUS_OPTIONS.filter((o) => o.value).map((o) => ({ value: o.value, label: o.label }))}
        />
      ),
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      width: 110,
      render: (v) => <Text style={{ fontSize: 12 }}>{formatDate(v)}</Text>,
    },
    {
      title: '',
      key: 'send',
      width: 80,
      render: (_, r) => (
        <Button
          size="small"
          icon={<SendOutlined />}
          onClick={(e) => { e.stopPropagation(); setNotifUser({ id: r.user_id, name: r.user_name }); }}
        >
          Send
        </Button>
      ),
    },
  ];

  const cfg = detailItem ? (STATUS_CONFIG[detailItem.status] || STATUS_CONFIG.pending) : null;

  return (
    <div>
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Help &amp; Suggestions</Title>
          <Text type="secondary" style={{ fontSize: 13 }}>Total: {meta.total} requests</Text>
        </div>
        <Space wrap>
          <Select
            value={status}
            onChange={setStatus}
            options={STATUS_OPTIONS}
            style={{ width: 140 }}
          />
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ width: 200 }}
          />
        </Space>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={items}
        loading={loading}
        scroll={{ x: 800 }}
        pagination={{
          current: meta.current_page,
          pageSize: meta.per_page,
          total: meta.total,
          showTotal: (total) => `${total} requests`,
          onChange: (page) => fetchHelp(page),
        }}
        size="small"
        onRow={(r) => ({ onClick: () => setDetailItem(r), style: { cursor: 'pointer' } })}
      />

      <Modal
        open={!!detailItem}
        onCancel={() => setDetailItem(null)}
        footer={null}
        width={520}
        title={
          <Space>
            {cfg?.icon}
            {detailItem?.title}
          </Space>
        }
      >
        {detailItem && (
          <div>
            <Space style={{ marginBottom: 16 }}>
              <Avatar
                src={detailItem.user_photo ? USER_IMAGE(detailItem.user_photo) : null}
                icon={<UserOutlined />}
                style={{ background: '#e8f5e9', color: '#1a6b2a' }}
              />
              <div>
                <div style={{ fontWeight: 600 }}>{detailItem.user_name}</div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>{detailItem.mobile}</div>
              </div>
            </Space>

            <div style={{ background: '#f9fafb', borderRadius: 8, padding: 14, marginBottom: 16 }}>
              <Paragraph style={{ margin: 0, fontSize: 14, color: '#374151' }}>{detailItem.description}</Paragraph>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>{formatDate(detailItem.created_at)}</Text>
              <Space>
                <Text style={{ fontSize: 13 }}>Update Status:</Text>
                <Select
                  value={detailItem.status}
                  loading={updating}
                  style={{ width: 150 }}
                  onChange={(val) => handleStatusChange(detailItem, val)}
                  options={STATUS_OPTIONS.filter((o) => o.value).map((o) => ({
                    value: o.value,
                    label: (
                      <Space>
                        {STATUS_CONFIG[o.value]?.icon}
                        {o.label}
                      </Space>
                    ),
                  }))}
                />
              </Space>
            </div>
          </div>
        )}
      </Modal>

      <SendNotificationModal
        open={!!notifUser}
        onClose={() => setNotifUser(null)}
        user={notifUser}
      />
    </div>
  );
}
